import { Form, Flex, Tooltip, Button, Input } from "antd";
import { DeleteOutlined, PlusOutlined, LinkOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useEffect, useRef } from "react";
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { useTranslation } from 'react-i18next';

// A link field holds a Form.List of { label } rows. A "set" action can arrive as
// a plain string (what the condition builder's value editor produces) or as an
// already-shaped list (a dynamic set reading another link field), so accept both
// and drop rows that carry no link.
const toLinkListValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return null
    }
    let rows = (Array.isArray(value) ? value : [value]).map(row => {
        if (typeof row === "string") {
            return { label: row }
        }
        if (row && typeof row === "object") {
            return { ...row, label: row.label || row.link || "" }
        }
        return null
    })
    rows = rows.filter(row => row && row.label)
    return rows.length > 0 ? rows : null
}

// Form.List renders `value || []`, so clearing to null would leave the field with
// no rows - and the "add link" button only exists inside a row, so there would be
// no way back. A single blank row is what the field starts life with.
const emptyLinkValue = () => [{ label: "" }]

const LinkInput = (props) => {
    const { t } = useTranslation();
    const { id, label, tooltip, helpText, mandatory, previewMode, readOnly, multipleLinksAllowed, form, formValues, placeholder, setValueData = [] } = props
    const rules = mandatory ? [{ required: true, whitespace: true }] : undefined;
    const disabled = previewMode && readOnly ? true : false
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;

    useEffect(() => {
        if(!multipleLinksAllowed && form && formValues && formValues[id]) {
            form.setFieldValue(id, [formValues?.[id]?.[0] || {link: ""}])
        }
    },[multipleLinksAllowed])

    const activeSetValue = getActiveSetValue(setValueData, true)

    // Track the condition value already applied so an editable field is prefilled
    // only once. Read-only fields stay locked to the condition value.
    const lastAppliedConditionalRef = useRef(undefined)

    useEffect(() => {
        const conditionalValue = toLinkListValue(activeSetValue?.value)
        if (conditionalValue) {
            const nextValue = multipleLinksAllowed ? conditionalValue : conditionalValue.slice(0, 1)
            const key = JSON.stringify(nextValue)
            if (disabled) {
                if (JSON.stringify(form?.getFieldValue(id)) !== key) {
                    form?.setFieldValue(id, nextValue)
                }
            } else if (lastAppliedConditionalRef.current !== key) {
                form?.setFieldValue(id, nextValue)
            }
            lastAppliedConditionalRef.current = key
        } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: JSON.stringify(form?.getFieldValue(id)), appliedValue: lastAppliedConditionalRef.current })) {
            // The condition that filled this field no longer passes, so drop the
            // links it set — or restore the field default if one was authored.
            const fallback = toLinkListValue(getConditionalRevertValue(id, props.defaultFormValues))
            const nextValue = fallback || emptyLinkValue()
            if (JSON.stringify(form?.getFieldValue(id)) !== JSON.stringify(nextValue)) {
                form?.setFieldValue(id, nextValue)
            }
            lastAppliedConditionalRef.current = undefined
        } else {
            lastAppliedConditionalRef.current = undefined
        }
    }, [activeSetValue, form, id, disabled, multipleLinksAllowed])

    const getItemValue = (index) => {
        let reqName = ""
        if(formValues?.[id]) {
            let currentValues = formValues?.[id]
            reqName = currentValues[index]?.label || ""
        }
        return reqName
    }

    const handleLinkClick = (index) => {
        let link = getItemValue(index)
        window.open(link, "_blank")
    }

    return (
        <div className="form-element">
            <Form.List name={id} initialValue={[{link: ""}]} >
                {(fields, { add, remove }) => (
                    <>
                            <Flex className="guidelines-section">
                                <div style={{ width: "100%" }}>
                                    {fields.map((field, index) => {
                                        return (
                                            <Flex gap={12} justify="space-between" align="flex-end">
                                                
                                                        <Form.Item label={index === 0 ? label : ''} name={[index, "label"]} tooltip={help} style={{ marginBottom: "20px", width: "100%" }} rules={[{ "required": mandatory, whitespace: true, message: "Link is required" }]}>
                                                            {
                                                                disabled ? <div className='info-editor-link' onClick={() => handleLinkClick(index)}>{getItemValue(index)}</div>
                                                                : <Input size="large" placeholder={placeholder || ""} prefix={<LinkOutlined />} suffix={index !== 0 ? <DeleteOutlined style={{ color: "red" }} onClick={() => remove(field.name)} /> : null} disabled={disabled} />
                                                            }
                                                        </Form.Item>
                                                        {
                                                            multipleLinksAllowed && !disabled ? 
                                                            <>
                                                                {
                                                                    index === fields.length - 1 ?
                                                                    <Form.Item style={{ width: "30px" }}>
                                                                        <Tooltip title={t('Add link')}>
                                                                            <Button size="small"
                                                                                onClick={() => add({ label: "" })}
                                                                                icon={<PlusOutlined />}
                                                                            />
                                                                        </Tooltip>
                                                                    </Form.Item>
                                                                    : <div style={{width: "30px"}}></div>
                                                                }
                                                            </>
                                                            
                                                            : null
                                                        }
                                            </Flex>
    
                                        )
                                    }
                                    
                                    )}

                                </div>
                            </Flex>

                    </>

                )}
            </Form.List>
        </div>
    )
}

export default LinkInput